import { useEffect, useMemo, useRef, useState } from "react";
import {
  addDays,
  addHours,
  addMinutes,
  addWeeks,
  differenceInMinutes,
  endOfWeek,
  format,
  isSameDay,
  max,
  min,
  startOfDay,
  startOfWeek,
  subDays,
  subWeeks,
} from "date-fns";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Filter,
  Layers,
  Plus,
  Truck,
  User,
} from "lucide-react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { TablePagination } from "@/shared/components/TablePagination";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
  useCreateDriverAvailability,
  useDriverAvailabilityCalendar,
  usePreviewDriverAvailabilityImpacts,
  useUpdateDriverAvailability,
} from "../hooks/useDriverAvailability";
import type {
  DriverAvailabilityStatus,
  DriverCalendarOverviewQuery,
  DriverCalendarResourceDto,
  DriverCalendarScheduleBlockDto,
} from "../services/driverAvailabilityApi";
import {
  getActionableScheduleBlocks,
  getBlockPrimaryDetail,
  getBlockSecondaryDetailForDay,
  getBlockTimeForDay,
  getCalendarDayBlocks,
  getDaySchedulingState,
  getDayStatusBlocks,
  getPrimaryScheduleBlock,
  getSchedulingState,
  isBlockAllDayForCalendarDay,
  type DriverSchedulingState,
  unavailableStatuses,
} from "./driverCalendarPresentation";

type CalendarMode = "day" | "week" | "compact";
type DensityMode = "comfortable" | "compact" | "dense";

type DriverCalendarResource = {
  resourceId: string;
  resourceTitle: string;
  phone: string;
  defaultVehicleReg?: string;
  currentStatus: DriverAvailabilityStatus;
  currentSourceType: string;
  hasConflictNow: boolean;
};

type ScheduleStatusFilter = "all" | Exclude<DriverSchedulingState, "Conflict">;

const statusList: DriverAvailabilityStatus[] = [
  "Available",
  "PartiallyAvailable",
  "Assigned",
  "Unavailable",
  "Sick",
  "OnLeave",
  "Other",
];

const schedulingStates: DriverSchedulingState[] = [
  "Available",
  "PartiallyAvailable",
  "Assigned",
  "NotScheduled",
  "Unavailable",
  "Conflict",
];

const statusLabel = (status: string) =>
  status === "NotScheduled"
    ? "Not scheduled"
    : status.replace(/([a-z])([A-Z])/g, "$1 $2");

const WORKING_HOURS = { start: 6, end: 20 };
const TIME_COLUMN_WIDTH = 96;
const DRIVER_COLUMN_WIDTH = 300;

const densityHeightPerHour: Record<DensityMode, number> = {
  comfortable: 72,
  compact: 56,
  dense: 44,
};

const densityStepMinutes: Record<DensityMode, number> = {
  comfortable: 60,
  compact: 30,
  dense: 15,
};

const schedulingStateToneClass: Record<DriverSchedulingState, string> = {
  Available: "border-emerald-300 bg-emerald-50 text-emerald-800",
  PartiallyAvailable: "border-amber-300 bg-amber-50 text-amber-800",
  Assigned: "border-blue-300 bg-blue-50 text-blue-800",
  NotScheduled: "border-gray-200 bg-gray-50 text-gray-600",
  Unavailable: "border-slate-300 bg-slate-100 text-slate-800",
  Conflict: "border-red-500 bg-red-50 text-red-800 ring-1 ring-inset ring-red-400",
};

const blockStateLabel = (block: DriverCalendarScheduleBlockDto) =>
  statusLabel(getSchedulingState([block]) ?? "NotScheduled");

const toLocalInputValue = (iso: string) => {
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const toIsoFromLocalInput = (value: string) => new Date(value).toISOString();

const buildDispatchQuery = (payload: {
  driverId: string;
  start: Date;
  end: Date;
  routeId?: string;
  vehicleId?: string;
  greenhouseId?: string;
  driverName?: string;
  routeName?: string;
  vehicleName?: string;
  greenhouseName?: string;
}) => {
  const params = new URLSearchParams();
  params.set("driverId", payload.driverId);
  params.set("start", format(payload.start, "yyyy-MM-dd'T'HH:mm"));
  params.set("end", format(payload.end, "yyyy-MM-dd'T'HH:mm"));
  if (payload.routeId) params.set("routeId", payload.routeId);
  if (payload.vehicleId) params.set("vehicleId", payload.vehicleId);
  if (payload.greenhouseId) params.set("greenhouseId", payload.greenhouseId);
  if (payload.driverName) params.set("driverName", payload.driverName);
  if (payload.routeName) params.set("routeName", payload.routeName);
  if (payload.vehicleName) params.set("vehicleName", payload.vehicleName);
  if (payload.greenhouseName) params.set("greenhouseName", payload.greenhouseName);
  return `/dispatch?${params.toString()}`;
};

const rangeForMode = (date: Date, mode: CalendarMode) => {
  const dayStart = startOfDay(date);
  if (mode === "day") {
    return {
      start: dayStart,
      end: addDays(dayStart, 1),
    };
  }

  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  return {
    start: weekStart,
    end: addDays(weekStart, 7),
  };
};

const modePeriodLabel = (date: Date, mode: CalendarMode) => {
  if (mode === "day") {
    return format(date, "EEE, dd MMM yyyy");
  }

  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
  return `${format(weekStart, "dd MMM")} - ${format(weekEnd, "dd MMM yyyy")}`;
};

const overlapsRange = (block: DriverCalendarScheduleBlockDto, start: Date, end: Date) => {
  const blockStart = new Date(block.startDateTime);
  const blockEnd = new Date(block.endDateTime);
  return blockStart < end && blockEnd > start;
};

const eventBaseClass = (block: DriverCalendarScheduleBlockDto) => {
  const state = getSchedulingState([block]) ?? "NotScheduled";
  return cn(
    "rounded-md border px-2 py-1 text-[11px] leading-tight text-left shadow-sm transition",
    schedulingStateToneClass[state]
  );
};

const DriverStatusBadge = ({
  status,
  sourceType,
  hasConflict,
}: {
  status: DriverAvailabilityStatus;
  sourceType: string;
  hasConflict: boolean;
}) => {
  const state: DriverSchedulingState = hasConflict
    ? "Conflict"
    : status === "Assigned"
      ? "Assigned"
      : status === "PartiallyAvailable"
        ? "PartiallyAvailable"
        : sourceType === "implicit_unavailable"
          ? "NotScheduled"
          : unavailableStatuses.includes(status)
            ? "Unavailable"
            : "Available";

  return (
    <div className="flex items-center gap-1.5">
      <Badge variant="outline" className={cn("text-[11px]", schedulingStateToneClass[state])}>
        {hasConflict && <AlertTriangle className="mr-1 h-3 w-3" />}
        {statusLabel(state)}
      </Badge>
      {!hasConflict && unavailableStatuses.includes(status) && status !== "Unavailable" && (
        <span className="text-[11px] font-medium text-muted-foreground">{statusLabel(status)}</span>
      )}
    </div>
  );
};

export const DriverAvailabilityCalendarOverview = ({ isActive = true }: { isActive?: boolean }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission("drivers.create");
  const canUpdate = hasPermission("drivers.update");

  const [mode, setMode] = useState<CalendarMode>("week");
  const [density, setDensity] = useState<DensityMode>("compact");
  const [date, setDate] = useState(new Date());
  const [showFullDay, setShowFullDay] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [driverId, setDriverId] = useState("all");
  const [routeId, setRouteId] = useState("all");
  const [vehicleId, setVehicleId] = useState("all");
  const [greenhouseId, setGreenhouseId] = useState("all");
  const [status, setStatus] = useState<ScheduleStatusFilter>("all");
  const [unavailabilityReason, setUnavailabilityReason] = useState<"all" | DriverAvailabilityStatus>("all");
  const [assignmentFilter, setAssignmentFilter] = useState<"all" | "assigned" | "unassigned">("all");
  const [conflictsOnly, setConflictsOnly] = useState(false);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(isMobile ? 6 : 12);

  const [selectedBlock, setSelectedBlock] = useState<DriverCalendarScheduleBlockDto | null>(null);
  const [selectedDayBlocks, setSelectedDayBlocks] = useState<{
    driverName: string;
    day: Date;
    blocks: DriverCalendarScheduleBlockDto[];
  } | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{ driverId: string; start: Date; end: Date } | null>(null);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [quickCreateForm, setQuickCreateForm] = useState({
    driverId: "",
    startDateTime: "",
    endDateTime: "",
    status: "Available" as DriverAvailabilityStatus,
    reason: "",
    notes: "",
  });

  const [editAvailabilityOpen, setEditAvailabilityOpen] = useState(false);
  const [editAvailabilityForm, setEditAvailabilityForm] = useState({
    startDateTime: "",
    endDateTime: "",
    status: "Available" as DriverAvailabilityStatus,
    reason: "",
    notes: "",
  });

  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const workspaceScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollSyncLockRef = useRef<"top" | "main" | null>(null);
  const driverHeaderRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const createMutation = useCreateDriverAvailability();
  const previewImpactMutation = usePreviewDriverAvailabilityImpacts();
  const updateMutation = useUpdateDriverAvailability();

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const baseRange = useMemo(() => rangeForMode(date, mode), [date, mode]);
  const queryView: "day" | "week" = mode === "day" ? "day" : "week";

  const requestedStatuses = useMemo<DriverAvailabilityStatus[] | undefined>(() => {
    if (unavailabilityReason !== "all") return [unavailabilityReason];
    if (status === "all") return undefined;
    if (status === "Unavailable") return unavailableStatuses;
    if (status === "NotScheduled") return ["Unavailable"];
    return [status as DriverAvailabilityStatus];
  }, [status, unavailabilityReason]);

  const query = useMemo<DriverCalendarOverviewQuery>(() => ({
    startDateTime: baseRange.start.toISOString(),
    endDateTime: baseRange.end.toISOString(),
    view: queryView,
    search: search || undefined,
    driverId: driverId === "all" ? undefined : driverId,
    routeId: routeId === "all" ? undefined : routeId,
    vehicleId: vehicleId === "all" ? undefined : vehicleId,
    greenhouseId: greenhouseId === "all" ? undefined : greenhouseId,
    statuses: requestedStatuses,
    activeDriversOnly: true,
    pageNumber,
    pageSize,
  }), [
    baseRange.start,
    baseRange.end,
    queryView,
    search,
    driverId,
    routeId,
    vehicleId,
    greenhouseId,
    requestedStatuses,
    pageNumber,
    pageSize,
  ]);

  const { data, isLoading, isFetching, error, refetch } = useDriverAvailabilityCalendar(query);

  useEffect(() => {
    if (!isActive) return;
    void refetch();
  }, [isActive, refetch]);

  const resources = useMemo<DriverCalendarResource[]>(() => {
    const rows = data?.drivers ?? [];
    return rows.map((driver: DriverCalendarResourceDto) => ({
      resourceId: driver.driverId,
      resourceTitle: driver.driverName,
      phone: driver.phone,
      defaultVehicleReg: driver.defaultVehicleReg,
      currentStatus: driver.currentStatus,
      currentSourceType: driver.currentSourceType,
      hasConflictNow: driver.hasConflictNow,
    }));
  }, [data?.drivers]);

  const blocks = useMemo(() => {
    const calendarBlocks = data?.blocks ?? [];
    return calendarBlocks.filter((block) => {
      const state = getSchedulingState([block]);

      if (unavailabilityReason !== "all") {
        if (state !== "Unavailable") return false;
        return block.status === unavailabilityReason;
      }

      return status === "all" || state === status;
    });
  }, [data?.blocks, status, unavailabilityReason]);

  const blocksByDriver = useMemo(() => {
    const map = new Map<string, DriverCalendarScheduleBlockDto[]>();
    for (const block of blocks) {
      const key = block.driverId;
      const list = map.get(key) ?? [];
      list.push(block);
      map.set(key, list);
    }

    for (const list of map.values()) {
      list.sort((a, b) =>
        new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
      );
    }

    return map;
  }, [blocks]);

  const actionableBlocksByDriver = useMemo(() => {
    const now = new Date();
    const map = new Map<string, DriverCalendarScheduleBlockDto[]>();
    for (const [resourceId, driverBlocks] of blocksByDriver) {
      map.set(resourceId, getActionableScheduleBlocks(driverBlocks, now));
    }
    return map;
  }, [blocksByDriver]);

  const assignmentCountByDriver = useMemo(() => {
    const countMap = new Map<string, number>();
    for (const resource of resources) {
      const matches = (actionableBlocksByDriver.get(resource.resourceId) ?? []).filter(
        (block) => block.status === "Assigned" || block.dispatches.length > 0
      );
      countMap.set(resource.resourceId, matches.length);
    }
    return countMap;
  }, [resources, actionableBlocksByDriver]);

  const conflictCountByDriver = useMemo(() => {
    const countMap = new Map<string, number>();
    for (const resource of resources) {
      const conflicts = (actionableBlocksByDriver.get(resource.resourceId) ?? []).filter((block) => block.isConflict);
      countMap.set(resource.resourceId, conflicts.length);
    }
    return countMap;
  }, [resources, actionableBlocksByDriver]);

  const locallyFilteredResources = useMemo(() => {
    return resources.filter((resource) => {
      const assignedCount = assignmentCountByDriver.get(resource.resourceId) ?? 0;
      const conflictCount = conflictCountByDriver.get(resource.resourceId) ?? 0;

      if (assignmentFilter === "assigned" && assignedCount === 0) return false;
      if (assignmentFilter === "unassigned" && assignedCount > 0) return false;
      if (conflictsOnly && conflictCount === 0) return false;
      return true;
    });
  }, [resources, assignmentFilter, conflictsOnly, assignmentCountByDriver, conflictCountByDriver]);

  const visibleDriverIds = useMemo(
    () => new Set(locallyFilteredResources.map((resource) => resource.resourceId)),
    [locallyFilteredResources]
  );

  const visibleBlocks = useMemo(
    () => blocks.filter((block) => visibleDriverIds.has(block.driverId)),
    [blocks, visibleDriverIds]
  );

  const visibleSummary = useMemo(() => {
    const summary = {
      total: locallyFilteredResources.length,
      available: 0,
      unavailable: 0,
      assigned: 0,
      notScheduled: 0,
      conflicts: 0,
    };

    for (const resource of locallyFilteredResources) {
      const periodBlocks = actionableBlocksByDriver.get(resource.resourceId) ?? [];
      if (periodBlocks.some((block) => block.status === "Available" || block.status === "PartiallyAvailable")) {
        summary.available++;
      }
      if (periodBlocks.some((block) => block.status === "Assigned" || block.dispatches.length > 0)) {
        summary.assigned++;
      }
      if (periodBlocks.some((block) => getSchedulingState([block]) === "Unavailable")) {
        summary.unavailable++;
      }
      if (periodBlocks.some((block) => getSchedulingState([block]) === "NotScheduled")) {
        summary.notScheduled++;
      }
      if (periodBlocks.some((block) => block.isConflict)) summary.conflicts++;
    }

    return summary;
  }, [locallyFilteredResources, actionableBlocksByDriver]);

  const periodLabel = useMemo(() => modePeriodLabel(date, mode), [date, mode]);

  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [date]);

  const greenhouseFilterOptions = useMemo(() => {
    const linkedIds = new Set<string>();
    for (const block of blocks) {
      for (const greenhouse of block.greenhouseIds) {
        linkedIds.add(greenhouse);
      }
    }

    const filtered = (data?.greenhouseOptions ?? []).filter(
      (greenhouse) => linkedIds.has(greenhouse.id) || greenhouse.id === greenhouseId
    );

    return filtered;
  }, [blocks, data?.greenhouseOptions, greenhouseId]);

  const showGreenhouseContextHint =
    greenhouseId !== "all" &&
    locallyFilteredResources.length > 0 &&
    visibleBlocks.length === 0;

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setDriverId("all");
    setRouteId("all");
    setVehicleId("all");
    setGreenhouseId("all");
    setStatus("all");
    setUnavailabilityReason("all");
    setAssignmentFilter("all");
    setConflictsOnly(false);
    setPageNumber(1);
  };

  const shiftPeriod = (dir: -1 | 1) => {
    setPageNumber(1);
    setDate((prev) => {
      if (mode === "day") {
        return dir > 0 ? addDays(prev, 1) : subDays(prev, 1);
      }
      return dir > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1);
    });
  };

  const syncTopScroll = () => {
    const top = topScrollRef.current;
    const main = workspaceScrollRef.current;
    if (!top || !main) return;
    if (scrollSyncLockRef.current === "main") return;
    scrollSyncLockRef.current = "top";
    main.scrollLeft = top.scrollLeft;
    requestAnimationFrame(() => {
      scrollSyncLockRef.current = null;
    });
  };

  const syncMainScroll = () => {
    const top = topScrollRef.current;
    const main = workspaceScrollRef.current;
    if (!top || !main) return;
    if (scrollSyncLockRef.current === "top") return;
    scrollSyncLockRef.current = "main";
    top.scrollLeft = main.scrollLeft;
    requestAnimationFrame(() => {
      scrollSyncLockRef.current = null;
    });
  };

  const scrollDriverIntoView = (targetDriverId: string) => {
    const node = driverHeaderRefs.current[targetDriverId];
    node?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  };

  useEffect(() => {
    if (driverId === "all") return;
    scrollDriverIntoView(driverId);
  }, [driverId, mode, locallyFilteredResources]);

  useEffect(() => {
    if (!search) return;
    const firstMatch = locallyFilteredResources[0];
    if (!firstMatch) return;
    scrollDriverIntoView(firstMatch.resourceId);
  }, [search, locallyFilteredResources, mode]);

  const openQuickCreateForSlot = (driver: DriverCalendarResource, start: Date, end: Date) => {
    setQuickCreateForm({
      driverId: driver.resourceId,
      startDateTime: toLocalInputValue(start.toISOString()),
      endDateTime: toLocalInputValue(end.toISOString()),
      status: "Available",
      reason: "",
      notes: "",
    });
    setSelectedSlot({ driverId: driver.resourceId, start, end });
    setQuickCreateOpen(true);
  };

  const openDispatchForSlot = () => {
    if (!selectedSlot) {
      navigate("/dispatch");
      return;
    }

    const slotDriver = resources.find((item) => item.resourceId === selectedSlot.driverId);

    navigate(
      buildDispatchQuery({
        driverId: selectedSlot.driverId,
        start: selectedSlot.start,
        end: selectedSlot.end,
        routeId: routeId === "all" ? undefined : routeId,
        vehicleId: vehicleId === "all" ? undefined : vehicleId,
        greenhouseId: greenhouseId === "all" ? undefined : greenhouseId,
        driverName: slotDriver?.resourceTitle,
        routeName:
          routeId === "all"
            ? undefined
            : (data?.routeOptions ?? []).find((item) => item.id === routeId)?.name,
        vehicleName:
          vehicleId === "all"
            ? undefined
            : (data?.vehicleOptions ?? []).find((item) => item.id === vehicleId)?.name,
        greenhouseName:
          greenhouseId === "all"
            ? undefined
            : (data?.greenhouseOptions ?? []).find((item) => item.id === greenhouseId)?.name,
      })
    );
  };

  const openDispatchForBlock = () => {
    if (!selectedBlock) {
      navigate("/dispatch");
      return;
    }

    navigate(
      buildDispatchQuery({
        driverId: selectedBlock.driverId,
        start: new Date(selectedBlock.startDateTime),
        end: new Date(selectedBlock.endDateTime),
        routeId: selectedBlock.routeId,
        vehicleId: selectedBlock.vehicleId,
        greenhouseId: selectedBlock.greenhouseIds[0],
        driverName: selectedBlock.driverName,
        routeName: selectedBlock.routeName,
        vehicleName: selectedBlock.vehicleRegistrationNumber,
        greenhouseName: selectedBlock.greenhouseNames[0],
      })
    );
  };

  const handleCreate = async () => {
    if (!quickCreateForm.driverId) {
      toast.error("Select a driver first.");
      return;
    }

    if (new Date(quickCreateForm.startDateTime) >= new Date(quickCreateForm.endDateTime)) {
      toast.error("End time must be later than start time.");
      return;
    }

    const payload = {
      driverId: quickCreateForm.driverId,
      startDateTime: toIsoFromLocalInput(quickCreateForm.startDateTime),
      endDateTime: toIsoFromLocalInput(quickCreateForm.endDateTime),
      status: quickCreateForm.status,
      reason: quickCreateForm.reason || undefined,
      notes: quickCreateForm.notes || undefined,
    };

    let requiresOverride = false;
    if (payload.status !== "Available") {
      const impacts = await previewImpactMutation.mutateAsync({
        driverId: payload.driverId,
        startDateTime: payload.startDateTime,
        endDateTime: payload.endDateTime,
        status: payload.status,
      });
      requiresOverride = impacts.requiresManagerOverride;
    }

    await createMutation.mutateAsync({
      ...payload,
      overrideAssignmentConflicts: requiresOverride,
      overrideReason: requiresOverride
        ? quickCreateForm.reason || quickCreateForm.notes || "Calendar override confirmed"
        : undefined,
    });

    setQuickCreateOpen(false);
  };

  const openEditAvailability = () => {
    if (!selectedBlock?.availabilityId || !selectedBlock.isEditable) return;

    setEditAvailabilityForm({
      startDateTime: toLocalInputValue(selectedBlock.startDateTime),
      endDateTime: toLocalInputValue(selectedBlock.endDateTime),
      status: selectedBlock.status,
      reason: selectedBlock.availabilityReason ?? "",
      notes: selectedBlock.availabilityNotes ?? "",
    });
    setEditAvailabilityOpen(true);
  };

  const handleEditAvailabilitySave = async () => {
    if (!selectedBlock?.availabilityId) return;

    if (new Date(editAvailabilityForm.startDateTime) >= new Date(editAvailabilityForm.endDateTime)) {
      toast.error("End time must be later than start time.");
      return;
    }

    await updateMutation.mutateAsync({
      id: selectedBlock.availabilityId,
      data: {
        startDateTime: toIsoFromLocalInput(editAvailabilityForm.startDateTime),
        endDateTime: toIsoFromLocalInput(editAvailabilityForm.endDateTime),
        status: editAvailabilityForm.status,
        reason: editAvailabilityForm.reason || undefined,
        notes: editAvailabilityForm.notes || undefined,
        isActive: true,
      },
    });

    setEditAvailabilityOpen(false);
  };

  const allFiltersDefault =
    !searchInput &&
    driverId === "all" &&
    routeId === "all" &&
    vehicleId === "all" &&
    greenhouseId === "all" &&
    status === "all" &&
    unavailabilityReason === "all" &&
    assignmentFilter === "all" &&
    !conflictsOnly;

  const secondaryFilterCount = [
    driverId !== "all",
    vehicleId !== "all",
    greenhouseId !== "all",
    assignmentFilter !== "all",
    unavailabilityReason !== "all",
  ].filter(Boolean).length;

  const dayWorkspace = useMemo(() => {
    const startHour = showFullDay ? 0 : WORKING_HOURS.start;
    const endHour = showFullDay ? 24 : WORKING_HOURS.end;

    const pxPerHour = densityHeightPerHour[density];
    const stepMinutes = densityStepMinutes[density];

    const dayStart = startOfDay(date);
    const displayStart = addHours(dayStart, startHour);
    const displayEnd = addHours(dayStart, endHour);

    const totalDisplayMinutes = Math.max(1, differenceInMinutes(displayEnd, displayStart));
    const rowCount = Math.max(1, Math.ceil(totalDisplayMinutes / stepMinutes));
    const rowHeight = (pxPerHour * stepMinutes) / 60;
    const timelineHeight = rowCount * rowHeight;
    const workspaceWidth =
      TIME_COLUMN_WIDTH + locallyFilteredResources.length * DRIVER_COLUMN_WIDTH;

    const now = new Date();
    const nowInDay = isSameDay(now, dayStart) && now >= displayStart && now <= displayEnd;
    const nowOffsetPx = nowInDay
      ? (differenceInMinutes(now, displayStart) / 60) * pxPerHour
      : null;

    const slotStarts = Array.from({ length: rowCount }, (_, idx) =>
      addMinutes(displayStart, idx * stepMinutes)
    );

    return {
      dayStart,
      displayStart,
      displayEnd,
      rowHeight,
      timelineHeight,
      workspaceWidth,
      nowOffsetPx,
      slotStarts,
      stepMinutes,
      pxPerHour,
    };
  }, [date, density, showFullDay, locallyFilteredResources.length]);

  const renderDayMode = () => {
    const {
      dayStart,
      displayStart,
      displayEnd,
      rowHeight,
      timelineHeight,
      workspaceWidth,
      nowOffsetPx,
      slotStarts,
      stepMinutes,
      pxPerHour,
    } = dayWorkspace;

    return (
      <div className="space-y-2">
        <div
          ref={topScrollRef}
          onScroll={syncTopScroll}
          className="h-3 overflow-x-auto overflow-y-hidden rounded bg-muted/60"
          aria-label="Top horizontal scrollbar"
        >
          <div style={{ width: workspaceWidth }} className="h-1" />
        </div>

        <div
          ref={workspaceScrollRef}
          onScroll={syncMainScroll}
          className="max-h-[68vh] overflow-auto rounded-md border bg-background shadow-sm"
        >
          <div style={{ width: workspaceWidth }} className="min-w-max">
            <div className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85">
              <div className="flex">
                <div
                  className="sticky left-0 z-40 border-r bg-background px-3 py-2"
                  style={{ width: TIME_COLUMN_WIDTH, minWidth: TIME_COLUMN_WIDTH }}
                >
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase">Time</p>
                  <p className="text-xs font-medium text-foreground">{format(dayStart, "EEE dd MMM")}</p>
                </div>

                {locallyFilteredResources.map((driver) => {
                  const assignments = assignmentCountByDriver.get(driver.resourceId) ?? 0;
                  const assignedVehicle =
                    driver.defaultVehicleReg ||
                    (blocksByDriver.get(driver.resourceId) ?? []).find((b) => b.vehicleRegistrationNumber)
                      ?.vehicleRegistrationNumber;

                  return (
                    <div
                      key={driver.resourceId}
                      ref={(el) => {
                        driverHeaderRefs.current[driver.resourceId] = el;
                      }}
                      className="border-l px-3 py-2"
                      style={{ width: DRIVER_COLUMN_WIDTH, minWidth: DRIVER_COLUMN_WIDTH }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold leading-tight">{driver.resourceTitle}</p>
                          <p className="text-[11px] text-muted-foreground">{driver.phone}</p>
                        </div>
                        {canCreate && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[11px]"
                            onClick={() =>
                              openQuickCreateForSlot(
                                driver,
                                addHours(dayStart, WORKING_HOURS.start),
                                addHours(dayStart, WORKING_HOURS.start + 8)
                              )
                            }
                            aria-label={`Add availability for ${driver.resourceTitle}`}
                          >
                            <Plus className="mr-1 h-3.5 w-3.5" />
                            Add
                          </Button>
                        )}
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] text-muted-foreground">Now</span>
                        <DriverStatusBadge
                          status={driver.currentStatus}
                          sourceType={driver.currentSourceType}
                          hasConflict={driver.hasConflictNow}
                        />
                      </div>

                      <div className="mt-2 grid grid-cols-2 gap-1 text-[11px] text-muted-foreground">
                        {assignedVehicle && (
                          <div className="inline-flex items-center gap-1">
                            <Truck className="h-3.5 w-3.5" />
                            <span>{assignedVehicle}</span>
                          </div>
                        )}
                        {assignments > 0 && (
                          <div className="inline-flex items-center gap-1">
                            <Layers className="h-3.5 w-3.5" />
                            <span>{assignments} {assignments === 1 ? "assignment" : "assignments"}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex border-t">
                <div
                  className="sticky left-0 z-40 border-r bg-background px-3 py-2 text-[11px] font-medium text-muted-foreground"
                  style={{ width: TIME_COLUMN_WIDTH, minWidth: TIME_COLUMN_WIDTH }}
                >
                  All day
                </div>

                {locallyFilteredResources.map((driver) => {
                  const dayBlocks = getCalendarDayBlocks(
                    blocksByDriver.get(driver.resourceId) ?? [],
                    dayStart
                  );
                  const allDayBlocks = dayBlocks.filter((block) =>
                    isBlockAllDayForCalendarDay(block, dayStart)
                  );

                  return (
                    <div
                      key={`all-day-${driver.resourceId}`}
                      className="border-l px-2 py-2"
                      style={{ width: DRIVER_COLUMN_WIDTH, minWidth: DRIVER_COLUMN_WIDTH }}
                    >
                      {allDayBlocks.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {allDayBlocks.slice(0, 2).map((block) => (
                            <button
                              type="button"
                              key={`${block.driverId}-${block.startDateTime}-${block.endDateTime}-${block.sourceType}`}
                              className={eventBaseClass(block)}
                              onClick={() => setSelectedBlock(block)}
                              title={`${blockStateLabel(block)} | ${getBlockPrimaryDetail(block) ?? "Schedule block"}`}
                            >
                              {blockStateLabel(block)}
                            </button>
                          ))}
                          {allDayBlocks.length > 2 && (
                            <Badge variant="outline" className="h-6 text-[10px]">
                              +{allDayBlocks.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <div className="flex">
                <div
                  className="sticky left-0 z-20 border-r bg-background"
                  style={{ width: TIME_COLUMN_WIDTH, minWidth: TIME_COLUMN_WIDTH, height: timelineHeight }}
                >
                  {slotStarts.map((slotStart, idx) => {
                    const isHourMark = slotStart.getMinutes() === 0;
                    return (
                      <div
                        key={`time-${idx}`}
                        className="border-t px-2"
                        style={{ height: rowHeight }}
                      >
                        <span className={cn("text-[11px]", isHourMark ? "font-medium text-foreground" : "text-muted-foreground/70")}>
                          {isHourMark ? format(slotStart, "h:mm a") : ""}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {locallyFilteredResources.map((driver) => {
                  const driverBlocks = getCalendarDayBlocks(
                    blocksByDriver.get(driver.resourceId) ?? [],
                    dayStart
                  )
                    .filter((block) => !isBlockAllDayForCalendarDay(block, dayStart));

                  return (
                    <div
                      key={`column-${driver.resourceId}`}
                      className="relative border-l"
                      style={{ width: DRIVER_COLUMN_WIDTH, minWidth: DRIVER_COLUMN_WIDTH, height: timelineHeight }}
                    >
                      {showFullDay && WORKING_HOURS.start > 0 && (
                        <div
                          className="pointer-events-none absolute inset-x-0 bg-muted/40"
                          style={{ top: 0, height: WORKING_HOURS.start * pxPerHour }}
                        />
                      )}
                      {showFullDay && WORKING_HOURS.end < 24 && (
                        <div
                          className="pointer-events-none absolute inset-x-0 bg-muted/40"
                          style={{
                            top: WORKING_HOURS.end * pxPerHour,
                            height: (24 - WORKING_HOURS.end) * pxPerHour,
                          }}
                        />
                      )}

                      {slotStarts.map((_, idx) => (
                        <button
                          key={`slot-${driver.resourceId}-${idx}`}
                          type="button"
                          className="absolute inset-x-0 border-t border-border/50 hover:bg-primary/5 focus-visible:bg-primary/10 focus-visible:outline-none"
                          style={{ top: idx * rowHeight, height: rowHeight }}
                          onClick={() => {
                            const slotStart = slotStarts[idx];
                            const slotEnd = addMinutes(slotStart, stepMinutes);
                            if (canCreate) {
                              openQuickCreateForSlot(driver, slotStart, slotEnd);
                            }
                          }}
                          aria-label={`Create availability slot for ${driver.resourceTitle} at ${format(slotStarts[idx], "h:mm a")}`}
                        />
                      ))}

                      {driverBlocks.map((block) => {
                        const rawStart = new Date(block.startDateTime);
                        const rawEnd = new Date(block.endDateTime);
                        const clampedStart = max([rawStart, displayStart]);
                        const clampedEnd = min([rawEnd, displayEnd]);

                        if (clampedEnd <= clampedStart) return null;

                        const offsetMins = differenceInMinutes(clampedStart, displayStart);
                        const durationMins = Math.max(1, differenceInMinutes(clampedEnd, clampedStart));
                        const top = (offsetMins / 60) * pxPerHour;
                        const height = Math.max(24, (durationMins / 60) * pxPerHour);
                        const compact = height < 44;

                        const reason = block.availabilityReason || block.deliveryPlanName || "No details";
                        const timeText = getBlockTimeForDay(block, dayStart)
                          ?.replace("–", " - ") ?? `${format(rawStart, "HH:mm")} - ${format(rawEnd, "HH:mm")}`;

                        return (
                          <button
                            key={`${block.driverId}-${block.startDateTime}-${block.endDateTime}-${block.sourceType}`}
                            type="button"
                            className={cn(eventBaseClass(block), "absolute left-1 right-1 z-10 overflow-hidden")}
                            style={{ top, height }}
                            onClick={() => setSelectedBlock(block)}
                            title={`${blockStateLabel(block)} | ${timeText} | ${reason}`}
                            aria-label={`${block.driverName} ${blockStateLabel(block)} from ${timeText}`}
                          >
                            <div className="flex items-center justify-between gap-1">
                              <span className="truncate font-semibold">{blockStateLabel(block)}</span>
                              {!compact && block.isConflict && <AlertTriangle className="h-3.5 w-3.5" />}
                            </div>
                            {!compact && (
                              <>
                                <p className="truncate text-[10px] opacity-90">{timeText}</p>
                                <p className="truncate text-[10px] opacity-85">{reason}</p>
                              </>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {typeof nowOffsetPx === "number" && (
                <div
                  className="pointer-events-none absolute right-0 left-[96px] z-20 border-t border-red-500"
                  style={{ top: nowOffsetPx }}
                >
                  <span className="-translate-y-1/2 rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-medium text-white">
                    Now
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const openWeekDay = (
    driver: DriverCalendarResource,
    day: Date,
    dayBlocks: DriverCalendarScheduleBlockDto[]
  ) => {
    if (dayBlocks.length === 1) {
      setSelectedBlock(dayBlocks[0]);
      return;
    }
    if (dayBlocks.length > 1) {
      setSelectedDayBlocks({ driverName: driver.resourceTitle, day, blocks: dayBlocks });
      return;
    }
    if (canCreate) {
      openQuickCreateForSlot(
        driver,
        addHours(day, WORKING_HOURS.start),
        addHours(day, WORKING_HOURS.start + 8)
      );
    }
  };

  const renderWeekOverview = () => {
    return (
      <div className="overflow-x-auto rounded-md border bg-background">
        <div className="min-w-[1320px]">
          <div className="sticky top-0 z-20 flex border-b bg-background/95 backdrop-blur">
            <div className="sticky left-0 z-30 w-[320px] min-w-[320px] border-r bg-background px-3 py-2 text-sm font-semibold">
              Driver
            </div>
            {weekDays.map((day) => {
              const weekend = day.getDay() === 0 || day.getDay() === 6;
              const isToday = isSameDay(day, new Date());
              return (
                <div
                  key={day.toISOString()}
                  className={cn(
                    "w-[142px] min-w-[142px] border-r px-2 py-2 text-center",
                    weekend ? "bg-muted/50" : "",
                    isToday ? "bg-primary/10" : ""
                  )}
                >
                  <p className="text-[11px] text-muted-foreground">{format(day, "EEE")}</p>
                  <p className="text-sm font-medium">{format(day, "dd MMM")}</p>
                </div>
              );
            })}
          </div>

          {locallyFilteredResources.map((driver) => {
            const assignments = assignmentCountByDriver.get(driver.resourceId) ?? 0;
            const conflicts = conflictCountByDriver.get(driver.resourceId) ?? 0;
            const driverBlocks = blocksByDriver.get(driver.resourceId) ?? [];

            return (
              <div key={`week-${driver.resourceId}`} className="flex border-b last:border-b-0">
                <div
                  ref={(el) => {
                    driverHeaderRefs.current[driver.resourceId] = el;
                  }}
                  className="sticky left-0 z-10 w-[320px] min-w-[320px] border-r bg-background px-3 py-3"
                >
                  <p className="text-sm font-semibold">{driver.resourceTitle}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{driver.phone}</span>
                    {assignments > 0 && (
                      <span className="inline-flex items-center gap-1 text-blue-700">
                        <Layers className="h-3 w-3" />
                        {assignments} {assignments === 1 ? "assignment" : "assignments"}
                      </span>
                    )}
                    {conflicts > 0 && (
                      <span className="inline-flex items-center gap-1 font-medium text-red-700">
                        <AlertTriangle className="h-3 w-3" />
                        {conflicts} {conflicts === 1 ? "conflict" : "conflicts"}
                      </span>
                    )}
                  </div>
                </div>

                {weekDays.map((day) => {
                  const statusNow = new Date();
                  const nextDay = addDays(day, 1);
                  const dayBlocks = getCalendarDayBlocks(driverBlocks, day);
                  const statusBlocks = getDayStatusBlocks(dayBlocks, day, statusNow);
                  const primaryBlock = getPrimaryScheduleBlock(statusBlocks);
                  const primaryState = getDaySchedulingState(dayBlocks, day, statusNow);
                  const primaryDetail = primaryBlock ? getBlockPrimaryDetail(primaryBlock) : undefined;
                  const secondaryDetail = primaryBlock
                    ? getBlockSecondaryDetailForDay(primaryBlock, day)
                    : undefined;
                  const continuesFromPrevious = primaryBlock
                    ? new Date(primaryBlock.startDateTime) < day
                    : false;
                  const continuesIntoNext = primaryBlock
                    ? new Date(primaryBlock.endDateTime) > nextDay
                    : false;
                  const isToday = isSameDay(day, statusNow);
                  const weekend = day.getDay() === 0 || day.getDay() === 6;
                  const isPrimaryCurrent = primaryBlock
                    ? new Date(primaryBlock.startDateTime) <= statusNow
                      && new Date(primaryBlock.endDateTime) > statusNow
                    : false;
                  const primaryStateLabel = primaryState === "Available" && isToday && isPrimaryCurrent
                    ? "Available now"
                    : primaryState
                      ? statusLabel(primaryState)
                      : undefined;

                  return (
                    <button
                      key={`week-cell-${driver.resourceId}-${day.toISOString()}`}
                      type="button"
                      onClick={() => openWeekDay(driver, day, dayBlocks)}
                      className={cn(
                        "relative min-h-[92px] w-[142px] min-w-[142px] border-r px-2 py-3 text-left align-top transition hover:bg-primary/5",
                        weekend ? "bg-muted/35" : "",
                        isToday ? "ring-1 ring-primary/30" : ""
                      )}
                      aria-label={`${driver.resourceTitle}, ${format(day, "EEEE dd MMM")}: ${primaryStateLabel ?? "No matching schedule"}${primaryDetail ? `, ${primaryDetail}` : ""}`}
                    >
                      {primaryState ? (
                        <div
                          className={cn(
                            "absolute inset-x-1 top-2 bottom-2 border px-2 py-1.5",
                            schedulingStateToneClass[primaryState],
                            continuesFromPrevious ? "left-0 rounded-l-none border-l-0" : "rounded-l-md",
                            continuesIntoNext ? "right-0 rounded-r-none border-r-0" : "rounded-r-md"
                          )}
                        >
                          <div className="flex items-center gap-1 font-semibold">
                            {primaryState === "Conflict" && <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
                            {primaryState === "Available" && <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />}
                            <span className="truncate">{primaryStateLabel}</span>
                          </div>
                          {primaryDetail && (
                            <p className="mt-1 truncate text-xs font-medium">{primaryDetail}</p>
                          )}
                          {secondaryDetail && (
                            <p className="truncate text-[11px] opacity-80">{secondaryDetail}</p>
                          )}
                          {statusBlocks.length > 1 && (
                            <p className="mt-1 text-[11px] font-medium">+{statusBlocks.length - 1} more</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {status === "all" && unavailabilityReason === "all" ? "No schedule" : "No matching status"}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCompactTimeline = () => {
    const timelineStart = startOfWeek(date, { weekStartsOn: 1 });
    const timelineEnd = addDays(timelineStart, 7);
    const totalMinutes = Math.max(1, differenceInMinutes(timelineEnd, timelineStart));

    const rowHeight = 68;
    const laneWidth = Math.max(1100, weekDays.length * 220);
    const workspaceWidth = 320 + laneWidth;

    return (
      <div className="space-y-2">
        <div
          ref={topScrollRef}
          onScroll={syncTopScroll}
          className="h-3 overflow-x-auto overflow-y-hidden rounded bg-muted/60"
          aria-label="Top horizontal scrollbar"
        >
          <div style={{ width: workspaceWidth }} className="h-1" />
        </div>

        <div
          ref={workspaceScrollRef}
          onScroll={syncMainScroll}
          className="max-h-[68vh] overflow-auto rounded-md border bg-background"
        >
          <div style={{ width: workspaceWidth }} className="min-w-max">
            <div className="sticky top-0 z-20 flex border-b bg-background/95 backdrop-blur">
              <div className="sticky left-0 z-30 w-[320px] min-w-[320px] border-r bg-background px-3 py-2 text-sm font-semibold">
                Driver timeline
              </div>
              <div className="relative" style={{ width: laneWidth, minWidth: laneWidth }}>
                <div className="flex">
                  {weekDays.map((day) => {
                    const weekend = day.getDay() === 0 || day.getDay() === 6;
                    const isToday = isSameDay(day, new Date());
                    return (
                      <div
                        key={`timeline-head-${day.toISOString()}`}
                        className={cn(
                          "flex-1 border-r px-2 py-2 text-center text-xs",
                          weekend ? "bg-muted/40" : "",
                          isToday ? "bg-primary/10 font-semibold" : "text-muted-foreground"
                        )}
                      >
                        {format(day, "EEE dd")}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {locallyFilteredResources.map((driver) => {
              const driverBlocks = (blocksByDriver.get(driver.resourceId) ?? []).filter((block) =>
                overlapsRange(block, timelineStart, timelineEnd)
              );
              const assignments = assignmentCountByDriver.get(driver.resourceId) ?? 0;
              const conflicts = conflictCountByDriver.get(driver.resourceId) ?? 0;

              return (
                <div key={`timeline-row-${driver.resourceId}`} className="flex border-b last:border-b-0">
                  <div
                    ref={(el) => {
                      driverHeaderRefs.current[driver.resourceId] = el;
                    }}
                    className="sticky left-0 z-10 w-[320px] min-w-[320px] border-r bg-background px-3 py-2"
                    style={{ height: rowHeight }}
                  >
                    <p className="text-sm font-semibold leading-tight">{driver.resourceTitle}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                      <span>{driver.phone}</span>
                      {assignments > 0 && <span className="text-blue-700">{assignments} assigned</span>}
                      {conflicts > 0 && (
                        <span className="font-medium text-red-700">
                          {conflicts} conflict{conflicts === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="relative" style={{ width: laneWidth, minWidth: laneWidth, height: rowHeight }}>
                    <div className="absolute inset-0 flex">
                      {weekDays.map((day) => {
                        const weekend = day.getDay() === 0 || day.getDay() === 6;
                        return (
                          <div
                            key={`timeline-grid-${driver.resourceId}-${day.toISOString()}`}
                            className={cn("flex-1 border-r", weekend ? "bg-muted/30" : "")}
                          />
                        );
                      })}
                    </div>

                    {driverBlocks.map((block) => {
                      const start = max([new Date(block.startDateTime), timelineStart]);
                      const end = min([new Date(block.endDateTime), timelineEnd]);
                      if (end <= start) return null;

                      const leftMinutes = differenceInMinutes(start, timelineStart);
                      const widthMinutes = Math.max(1, differenceInMinutes(end, start));
                      const leftPct = (leftMinutes / totalMinutes) * 100;
                      const widthPct = (widthMinutes / totalMinutes) * 100;

                      const summary = `${blockStateLabel(block)} • ${format(new Date(block.startDateTime), "EEE HH:mm")} - ${format(new Date(block.endDateTime), "EEE HH:mm")}`;

                      return (
                        <button
                          key={`${block.driverId}-${block.startDateTime}-${block.endDateTime}-${block.sourceType}`}
                          type="button"
                          className={cn(eventBaseClass(block), "absolute top-2 h-14")}
                          style={{
                            left: `calc(${leftPct}% + 2px)`,
                            width: `max(42px, calc(${widthPct}% - 4px))`,
                          }}
                          onClick={() => setSelectedBlock(block)}
                          title={summary}
                          aria-label={`${block.driverName} ${summary}`}
                        >
                          <p className="truncate font-semibold">{blockStateLabel(block)}</p>
                          <p className="truncate text-[10px]">{block.routeName ?? block.deliveryPlanName ?? block.availabilityReason ?? "Schedule block"}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Driver Scheduling Workspace</CardTitle>
          <CardDescription>Loading availability intelligence...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 py-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="h-20 animate-pulse rounded-md border bg-muted/50" />
            ))}
          </div>
          <div className="h-[460px] animate-pulse rounded-md border bg-muted/50" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="space-y-3 py-10 text-center">
          <p className="text-sm text-red-700">Unable to load driver availability calendar right now.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Drivers shown</CardDescription>
            <CardTitle className="text-2xl">{visibleSummary.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Available / partial in period</CardDescription>
            <CardTitle className="text-2xl text-emerald-700">{visibleSummary.available}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Assigned in period</CardDescription>
            <CardTitle className="text-2xl text-blue-700">{visibleSummary.assigned}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Not scheduled in period</CardDescription>
            <CardTitle className="text-2xl text-gray-600">{visibleSummary.notScheduled}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Unavailable in period</CardDescription>
            <CardTitle className="text-2xl text-amber-700">{visibleSummary.unavailable}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Conflicts in period</CardDescription>
            <CardTitle className="text-2xl text-red-700">{visibleSummary.conflicts}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card className="py-0">
        <CardHeader className="space-y-4 border-b py-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Driver schedule</CardTitle>
              <CardDescription className="mt-1 flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" />
                {periodLabel}
                {isFetching && <span>· Refreshing…</span>}
              </CardDescription>
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <Button variant="outline" size="sm" onClick={() => shiftPeriod(-1)}>
                <ChevronLeft className="mr-1 h-4 w-4" />
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setDate(new Date()); setPageNumber(1); }}>Today</Button>
              <Button variant="outline" size="sm" onClick={() => shiftPeriod(1)}>
                Next
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>

              <Input
                type="date"
                aria-label="Pick date"
                className="h-8 w-[160px]"
                value={format(date, "yyyy-MM-dd")}
                onChange={(e) => {
                  const next = new Date(`${e.target.value}T12:00:00`);
                  if (!Number.isNaN(next.getTime())) {
                    setDate(next);
                    setPageNumber(1);
                  }
                }}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant={mode === "day" ? "default" : "outline"}
              onClick={() => { setMode("day"); setPageNumber(1); }}
            >
              Day
            </Button>
            <Button
              size="sm"
              variant={mode === "week" ? "default" : "outline"}
              onClick={() => { setMode("week"); setPageNumber(1); }}
            >
              Week
            </Button>
            <Button
              size="sm"
              variant={mode === "compact" ? "default" : "outline"}
              onClick={() => { setMode("compact"); setPageNumber(1); }}
            >
              Compact
            </Button>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <Input
              placeholder="Search driver name"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPageNumber(1);
              }}
              aria-label="Search driver by name"
            />

            <Select
              value={status}
              onValueChange={(value) => {
                const nextStatus = value as ScheduleStatusFilter;
                setStatus(nextStatus);
                if (nextStatus !== "Unavailable") setUnavailabilityReason("all");
                setPageNumber(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {schedulingStates.filter((item) => item !== "Conflict").map((item) => (
                  <SelectItem key={item} value={item}>
                    {statusLabel(item)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={routeId}
              onValueChange={(value) => {
                setRouteId(value);
                setPageNumber(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="All routes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All routes</SelectItem>
                {(data?.routeOptions ?? []).map((route) => (
                  <SelectItem key={route.id} value={route.id}>{route.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center justify-between rounded-md border px-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="conflicts-only"
                  checked={conflictsOnly}
                  onCheckedChange={(value) => { setConflictsOnly(value === true); setPageNumber(1); }}
                />
                <Label htmlFor="conflicts-only" className="text-sm">Conflicts only</Label>
              </div>
            </div>
          </div>

          <Collapsible open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
            <CollapsibleTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className="w-fit">
                <Filter className="mr-1.5 h-4 w-4" />
                More filters
                {secondaryFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 min-w-5 px-1.5">
                    {secondaryFilterCount}
                  </Badge>
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <div className="grid gap-3 rounded-md border bg-muted/20 p-3 md:grid-cols-2 xl:grid-cols-5">
                <Select value={driverId} onValueChange={(value) => { setDriverId(value); setPageNumber(1); }}>
                  <SelectTrigger><SelectValue placeholder="All drivers" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All drivers</SelectItem>
                    {(data?.drivers ?? []).map((driver) => (
                      <SelectItem key={driver.driverId} value={driver.driverId}>{driver.driverName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={vehicleId} onValueChange={(value) => { setVehicleId(value); setPageNumber(1); }}>
                  <SelectTrigger><SelectValue placeholder="All vehicles" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All vehicles</SelectItem>
                    {(data?.vehicleOptions ?? []).map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>{vehicle.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={greenhouseId} onValueChange={(value) => { setGreenhouseId(value); setPageNumber(1); }}>
                  <SelectTrigger><SelectValue placeholder="All greenhouses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All greenhouses</SelectItem>
                    {greenhouseFilterOptions.map((greenhouse) => (
                      <SelectItem key={greenhouse.id} value={greenhouse.id}>{greenhouse.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={assignmentFilter} onValueChange={(value) => { setAssignmentFilter(value as "all" | "assigned" | "unassigned"); setPageNumber(1); }}>
                  <SelectTrigger><SelectValue placeholder="Assignment" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any assignment state</SelectItem>
                    <SelectItem value="assigned">Assigned only</SelectItem>
                    <SelectItem value="unassigned">Unassigned only</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={unavailabilityReason}
                  onValueChange={(value) => {
                    setUnavailabilityReason(value as "all" | DriverAvailabilityStatus);
                    if (value !== "all") setStatus("Unavailable");
                    setPageNumber(1);
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Any unavailable reason" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any unavailable reason</SelectItem>
                    <SelectItem value="Sick">Sick</SelectItem>
                    <SelectItem value="OnLeave">On Leave</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                    <SelectItem value="Unavailable">Unspecified</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="flex flex-wrap items-center gap-2">
            {!allFiltersDefault && (
              <Button type="button" variant="outline" size="sm" onClick={resetFilters}>
                Clear filters
              </Button>
            )}

            <Badge variant="outline">Visible drivers: {visibleSummary.total}</Badge>

            {mode === "day" && (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant={showFullDay ? "outline" : "default"}
                  onClick={() => setShowFullDay((prev) => !prev)}
                >
                  {showFullDay ? "Working hours only" : "Show full 24h"}
                </Button>

                <Select
                  value={density}
                  onValueChange={(value) => setDensity(value as DensityMode)}
                >
                  <SelectTrigger className="h-8 w-[150px]">
                    <SelectValue placeholder="Density" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="comfortable">Zoom: Comfortable</SelectItem>
                    <SelectItem value="compact">Zoom: Compact</SelectItem>
                    <SelectItem value="dense">Zoom: Dense</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2" aria-label="Scheduling status legend">
            <span className="text-xs font-medium text-muted-foreground">Status</span>
            {schedulingStates.map((item) => (
              <Badge key={item} variant="outline" className={cn("text-xs", schedulingStateToneClass[item])}>
                {item === "Conflict" && <AlertTriangle className="mr-1 h-3.5 w-3.5" />}
                {statusLabel(item)}
              </Badge>
            ))}
          </div>

          {showGreenhouseContextHint && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Greenhouse filtering can be incomplete for schedule blocks that are not linked to dispatch stock lineage.
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-4 py-4">
          {locallyFilteredResources.length === 0 ? (
            <div className="rounded-md border border-dashed bg-muted/30 px-6 py-10 text-center">
              <p className="text-sm font-medium">No drivers match the selected filters.</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Try clearing one or more filters, or switch to another period.
              </p>
            </div>
          ) : isMobile ? (
            <div className="space-y-3">
              {locallyFilteredResources.map((driver) => {
                const mobileBlocks = (blocksByDriver.get(driver.resourceId) ?? [])
                  .filter((block) => overlapsRange(block, baseRange.start, baseRange.end))
                  .sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime());

                return (
                  <Card key={`mobile-${driver.resourceId}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">{driver.resourceTitle}</CardTitle>
                      <CardDescription>{driver.phone}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">Now</span>
                        <DriverStatusBadge
                          status={driver.currentStatus}
                          sourceType={driver.currentSourceType}
                          hasConflict={driver.hasConflictNow}
                        />
                      </div>

                      {mobileBlocks.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No availability blocks in this period.</p>
                      ) : (
                        <div className="space-y-2">
                          {mobileBlocks.slice(0, 6).map((block) => (
                            <button
                              type="button"
                              key={`mobile-block-${block.driverId}-${block.startDateTime}-${block.endDateTime}`}
                              className={cn(eventBaseClass(block), "w-full")}
                              onClick={() => setSelectedBlock(block)}
                            >
                              <p className="truncate font-semibold">{blockStateLabel(block)}</p>
                              <p className="truncate text-[10px]">
                                {format(new Date(block.startDateTime), "EEE HH:mm")} - {format(new Date(block.endDateTime), "EEE HH:mm")}
                              </p>
                              {getBlockPrimaryDetail(block) && (
                                <p className="truncate text-[10px]">{getBlockPrimaryDetail(block)}</p>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {canCreate && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            openQuickCreateForSlot(
                              driver,
                              addHours(startOfDay(date), WORKING_HOURS.start),
                              addHours(startOfDay(date), WORKING_HOURS.start + 8)
                            )
                          }
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add availability
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : mode === "day" ? (
            renderDayMode()
          ) : mode === "week" ? (
            renderWeekOverview()
          ) : (
            renderCompactTimeline()
          )}

          <TablePagination
            pageNumber={pageNumber}
            pageSize={pageSize}
            totalCount={data?.totalDrivers ?? 0}
            totalPages={data?.totalPages ?? 0}
            onPageChange={setPageNumber}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setPageNumber(1);
            }}
            pageSizeOptions={isMobile ? [5, 10, 15] : [8, 12, 20, 30]}
            isFetching={isFetching}
          />
        </CardContent>
      </Card>

      <Dialog open={!!selectedDayBlocks} onOpenChange={(open) => !open && setSelectedDayBlocks(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{selectedDayBlocks?.driverName}</DialogTitle>
            <DialogDescription>
              {selectedDayBlocks ? format(selectedDayBlocks.day, "EEEE, dd MMMM yyyy") : ""} schedule
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {selectedDayBlocks?.blocks.map((block) => (
              <button
                type="button"
                key={`day-detail-${block.driverId}-${block.startDateTime}-${block.endDateTime}-${block.sourceType}`}
                className={cn(eventBaseClass(block), "w-full p-3")}
                onClick={() => {
                  setSelectedDayBlocks(null);
                  setSelectedBlock(block);
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold">{blockStateLabel(block)}</span>
                  <span>{format(new Date(block.startDateTime), "HH:mm")}–{format(new Date(block.endDateTime), "HH:mm")}</span>
                </div>
                {getBlockPrimaryDetail(block) && (
                  <p className="mt-1 truncate text-xs">{getBlockPrimaryDetail(block)}</p>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedBlock} onOpenChange={(open) => !open && setSelectedBlock(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Schedule Block Details</DialogTitle>
            <DialogDescription>
              {selectedBlock?.driverName} | {selectedBlock ? blockStateLabel(selectedBlock) : ""}
            </DialogDescription>
          </DialogHeader>

          {selectedBlock && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
                <div>
                  <p className="text-muted-foreground">Start</p>
                  <p>{new Date(selectedBlock.startDateTime).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">End</p>
                  <p>{new Date(selectedBlock.endDateTime).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Source</p>
                  <p>
                    {selectedBlock.sourceType === "implicit_unavailable"
                      ? "No availability allocation"
                      : selectedBlock.sourceType.replace(/_/g, " ")}
                  </p>
                </div>
                {selectedBlock.isConflict && (
                  <div>
                    <p className="text-muted-foreground">Conflict</p>
                    <p className="font-medium text-red-700">Needs attention</p>
                  </div>
                )}
              </div>

              {selectedBlock.deliveryPlanName && (
                <div className="rounded-md border p-3">
                  <p className="font-medium">Delivery Plan</p>
                  <p>{selectedBlock.deliveryPlanName}</p>
                  <p className="text-muted-foreground">Route: {selectedBlock.routeName ?? "-"}</p>
                  <p className="text-muted-foreground">Vehicle: {selectedBlock.vehicleRegistrationNumber ?? "-"}</p>
                </div>
              )}

              {selectedBlock.dispatches.length > 0 && (
                <div className="rounded-md border p-3">
                  <p className="font-medium">Linked Dispatches</p>
                  <div className="mt-2 space-y-1">
                    {selectedBlock.dispatches.map((dispatch) => (
                      <p key={dispatch.dispatchRecordId}>
                        {dispatch.dispatchNumber} ({dispatch.status})
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {selectedBlock.greenhouseNames.length > 0 && (
                <div>
                  <p className="text-muted-foreground">Greenhouses</p>
                  <p>{selectedBlock.greenhouseNames.join(", ")}</p>
                </div>
              )}

              {selectedBlock.availabilityReason && (
                <div>
                  <p className="text-muted-foreground">Reason</p>
                  <p>{selectedBlock.availabilityReason}</p>
                </div>
              )}

              {selectedBlock.availabilityNotes && (
                <div>
                  <p className="text-muted-foreground">Notes</p>
                  <p>{selectedBlock.availabilityNotes}</p>
                </div>
              )}

              {selectedBlock.conflictReason && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700">
                  {selectedBlock.conflictReason}
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-2">
                <Button type="button" variant="outline" onClick={openDispatchForBlock}>
                  Open Dispatch Board
                </Button>
                {canUpdate && selectedBlock.isEditable && selectedBlock.availabilityId && (
                  <Button type="button" onClick={openEditAvailability}>
                    Edit Availability Window
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editAvailabilityOpen} onOpenChange={setEditAvailabilityOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Availability Window</DialogTitle>
            <DialogDescription>Update the selected availability segment.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <Field>
                <FieldLabel>Start</FieldLabel>
                <DateTimePicker
                  id="calendar-edit-availability-start"
                  value={editAvailabilityForm.startDateTime}
                  onChange={(value) =>
                    setEditAvailabilityForm((prev) => ({ ...prev, startDateTime: value }))
                  }
                />
              </Field>
              <Field>
                <FieldLabel>End</FieldLabel>
                <DateTimePicker
                  id="calendar-edit-availability-end"
                  value={editAvailabilityForm.endDateTime}
                  onChange={(value) =>
                    setEditAvailabilityForm((prev) => ({ ...prev, endDateTime: value }))
                  }
                  min={editAvailabilityForm.startDateTime || undefined}
                />
              </Field>
            </div>

            <Field>
              <FieldLabel>Status</FieldLabel>
              <Select
                value={editAvailabilityForm.status}
                onValueChange={(value) =>
                  setEditAvailabilityForm((prev) => ({ ...prev, status: value as DriverAvailabilityStatus }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusList.map((item) => (
                    <SelectItem key={item} value={item}>{statusLabel(item)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <FieldLabel>Reason</FieldLabel>
              <Textarea
                value={editAvailabilityForm.reason}
                onChange={(e) =>
                  setEditAvailabilityForm((prev) => ({ ...prev, reason: e.target.value }))
                }
                rows={2}
              />
            </Field>

            <Field>
              <FieldLabel>Notes</FieldLabel>
              <Textarea
                value={editAvailabilityForm.notes}
                onChange={(e) =>
                  setEditAvailabilityForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                rows={3}
              />
            </Field>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditAvailabilityOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleEditAvailabilitySave}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={quickCreateOpen} onOpenChange={setQuickCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Availability from Calendar</DialogTitle>
            <DialogDescription>
              Create an availability window for this slot, or navigate to dispatch for assignment workflows.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Field>
              <FieldLabel>Driver</FieldLabel>
              <Select
                value={quickCreateForm.driverId}
                onValueChange={(value) => setQuickCreateForm((prev) => ({ ...prev, driverId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  {(data?.drivers ?? []).map((driver) => (
                    <SelectItem key={driver.driverId} value={driver.driverId}>{driver.driverName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="grid gap-3 md:grid-cols-2">
              <Field>
                <FieldLabel>Start</FieldLabel>
                <DateTimePicker
                  id="calendar-create-availability-start"
                  value={quickCreateForm.startDateTime}
                  onChange={(value) => setQuickCreateForm((prev) => ({ ...prev, startDateTime: value }))}
                />
              </Field>

              <Field>
                <FieldLabel>End</FieldLabel>
                <DateTimePicker
                  id="calendar-create-availability-end"
                  value={quickCreateForm.endDateTime}
                  onChange={(value) => setQuickCreateForm((prev) => ({ ...prev, endDateTime: value }))}
                  min={quickCreateForm.startDateTime || undefined}
                />
              </Field>
            </div>

            <Field>
              <FieldLabel>Status</FieldLabel>
              <Select
                value={quickCreateForm.status}
                onValueChange={(value) =>
                  setQuickCreateForm((prev) => ({ ...prev, status: value as DriverAvailabilityStatus }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusList.map((item) => (
                    <SelectItem key={item} value={item}>{statusLabel(item)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <FieldLabel>Reason</FieldLabel>
              <Textarea
                value={quickCreateForm.reason}
                onChange={(e) => setQuickCreateForm((prev) => ({ ...prev, reason: e.target.value }))}
                rows={2}
              />
            </Field>

            <Field>
              <FieldLabel>Notes</FieldLabel>
              <Textarea
                value={quickCreateForm.notes}
                onChange={(e) => setQuickCreateForm((prev) => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </Field>

            <div className="flex flex-wrap justify-between gap-2 pt-2">
              <Button type="button" variant="outline" onClick={openDispatchForSlot}>
                <Plus className="mr-2 h-4 w-4" />
                Open Dispatch Board
              </Button>

              <Button
                type="button"
                onClick={handleCreate}
                disabled={createMutation.isPending || previewImpactMutation.isPending}
              >
                {createMutation.isPending || previewImpactMutation.isPending ? "Saving..." : "Save Availability"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
